/* Drag to rotate.
 *
 * A turntable is a sequence of stills, not a video: the visitor's pointer
 * has to map to a frame index directly, and seeking a compressed video is
 * both laggy and unsupported on the browsers that matter least but complain
 * most. So the frames arrive as one JSON payload of data URIs, which is a
 * single request, cacheable, and immune to Shopify transcoding whatever it
 * feels like transcoding that week.
 *
 * That payload is over a megabyte, so the first frame ships in the markup as
 * an ordinary image and the section paints without it. The sequence is then
 * fetched on the first of: the page going idle, or the stage coming within
 * 1200px of the viewport. Waiting for the scroll alone left a window where
 * the object was on screen, static, and silently refusing to be dragged,
 * which reads as broken rather than as loading.
 *
 * Until it lands the still is the whole picture, which is also exactly what
 * a reduced-motion visitor keeps.
 *
 * The idle spin exists so nobody has to guess the thing is interactive. It
 * stops for good on the first touch, because after that the visitor is
 * driving and anything else fighting them is rude.
 */
(function () {
  'use strict';

  var stages = document.querySelectorAll('[data-ak-tt]');
  if (!stages.length) return;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* How long the unattended spin takes to get all the way round, in seconds.
     Held as a duration rather than a frame rate so a shorter sequence does
     not idle faster than a longer one: the rate is derived per instance from
     however many frames it actually has. 7.2s reads as an object idling. */
  var TURN_SECONDS = 7.2;

  function setup(stage) {
    var img = stage.querySelector('[data-ak-tt-img]');
    var src = stage.getAttribute('data-ak-tt-src');
    if (!img || !src) return;
    /* A page can pull this script in from more than one place, and binding
       the same stage twice gives it two idle loops fighting each other. */
    if (stage.dataset.akTtInit) return;
    stage.dataset.akTtInit = '1';

    var frames = [];
    var canvas = null;
    var ctx = null;
    var loaded = false;
    var driving = false;
    var pendingDrag = null;
    var startX = 0;
    var dragFrom = 0;

    /* Position is a FLOAT frame index, drawn to the nearest whole one. The
       fraction is what makes the hand and the flick continuous: a drag maps
       to a real number of frames and momentum decays smoothly through it,
       rather than everything snapping in whole-frame jumps. How smooth the
       RESULT looks is then purely a function of how many frames the sequence
       has, which is the only honest lever. */
    var pos = 0;
    var painted = -1;   // last frame INDEX drawn, so a repeat is free
    var raf = 0;
    var lastT = 0;
    var ambient = false;   // the unattended spin is wanted right now
    var touched = false;   // they have driven it, so the ambient spin is done
    var velocity = 0;      // frames per second, decaying, after a flick
    var samples = [];      // recent {t, pos} for working out that velocity

    /* Direction applies to the unattended spin only. A drag, a flick and the
       arrow keys always follow the hand, whichever way the object idles. */
    function idleRate() {
      var dir = stage.getAttribute('data-ak-tt-spin') === 'reverse' ? -1 : 1;
      return dir * frames.length / TURN_SECONDS;
    }

    /* How fast a flick may leave the hand, in turns per second. Without a
       ceiling a fast swipe on a phone spins the object into a blur. */
    var MAX_TURNS = 1.5;
    /* Share of its speed a flick keeps per 60Hz frame. 0.95 puts it at about
       5% after a second, which reads as a bearing rather than a brake. */
    var FRICTION = 0.95;

    /* One frame, hard. No dissolve between neighbours: blending two angles
       together to fake the in-between reads as smeared rather than as smooth,
       and a product render has to look like the product. Smoothness here is
       bought with frames, not with alpha. Position still carries its fraction
       so the hand maps continuously and momentum decays continuously; only
       the drawing is quantised. */
    function paint() {
      if (!loaded) return;
      var n = frames.length;
      var i = ((Math.round(pos) % n) + n) % n;
      if (i === painted) return;

      if (!ctx) { img.src = frames[i].src; painted = i; return; }

      /* Blitting an already decoded bitmap is synchronous, so the old frame
         is never cleared before the new one exists. Assigning img.src instead
         hands the element back to the decoder for a beat, and that beat is
         the flicker. */
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(frames[i], 0, 0, canvas.width, canvas.height);
      painted = i;
    }

    /* One rotation per stage width dragged, which is the ratio that feels
       like you are turning the object rather than scrubbing a timeline. No
       rounding: the fraction is the whole point. */
    function framesFor(dx) {
      var w = stage.clientWidth || 1;
      return dx / w * frames.length;
    }

    function schedule() {
      if (!raf) raf = window.requestAnimationFrame(tick);
    }

    function halt() {
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
      lastT = 0;
      velocity = 0;
    }

    /* One loop drives everything: the hand, the flick and the ambient spin.
       It runs at the display's rate while any of those is live and stops
       dead when none is, so an object sitting still costs nothing. */
    function tick(now) {
      raf = 0;
      var dt = lastT ? Math.min((now - lastT) / 1000, 0.05) : 0;
      lastT = now;
      var moving = false;

      if (driving) {
        moving = true;                      // pointermove owns pos
      } else if (velocity) {
        pos += velocity * dt;
        velocity *= Math.pow(FRICTION, dt * 60);
        if (Math.abs(velocity) < 0.5) velocity = 0;
        moving = true;
      } else if (ambient && !touched && !reduce.matches && loaded) {
        pos += idleRate() * dt;
        moving = true;
      }

      if (moving) {
        /* paint() is a no-op when the rounded frame has not changed, so the
           loop can run at the display's rate and still only draw on a real
           frame change. */
        paint();
        schedule();
      } else {
        lastT = 0;
      }
    }

    function startAmbient() {
      if (touched || reduce.matches || !loaded) return;
      ambient = true;
      schedule();
    }

    function load() {
      if (loaded || stage.dataset.akTtLoading) return;
      stage.dataset.akTtLoading = '1';
      fetch(src, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data || !data.frames || !data.frames.length) return;
          /* The still ships with a srcset so it is responsive before the
             sequence exists. Setting .src on an element that still has a
             srcset changes nothing on screen: the browser re-runs the
             selection algorithm and keeps painting the srcset candidate.
             Drop both attributes in case we fall back to the img path. */
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');

          ready(data);
        })
        .catch(function () { /* the still remains the whole picture */ });
    }

    /* Decode every frame before declaring the thing draggable. A rotation
       that has to wait on a decoder stutters, and the first drag is the one
       that decides whether the visitor believes it works. */
    function ready(data) {
      var pending = data.frames.length;
      var decoded = new Array(pending);

      data.frames.forEach(function (src, i) {
        var im = new Image();
        im.onload = im.onerror = function () {
          decoded[i] = im;
          if (--pending === 0) finish(data, decoded);
        };
        im.src = src;
      });
    }

    function finish(data, decoded) {
      frames = decoded;

      canvas = document.createElement('canvas');
      canvas.className = 'ak-tt__canvas';
      canvas.width = data.w;
      canvas.height = data.h;
      ctx = canvas.getContext('2d');
      if (ctx) {
        stage.insertBefore(canvas, stage.firstChild);
        img.hidden = true;
        /* Keep the box exactly the frame's shape so the swap shifts nothing.
           Not when the stage is filling something else's box, though: there
           the container owns the shape, the object is letterboxed inside it
           by object-fit, and an inline aspect-ratio here would beat every
           stylesheet rule and burst the stage out of its container. */
        if (!stage.hasAttribute('data-ak-tt-fill')) {
          stage.style.aspectRatio = data.w + ' / ' + data.h;
        }
      }

      loaded = true;
      stage.classList.remove('is-waiting');
      stage.classList.add('is-ready');
      pos = 0;
      paint();

      if (pendingDrag) {
        driving = true;
        touched = true;
        startX = pendingDrag.x;
        dragFrom = 0;
        stage.classList.add('is-driving');
        pendingDrag = null;
        schedule();
        return;   // they are already holding it; do not start the idle spin
      }
      startAmbient();
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          /* Off screen nothing may run, a decaying flick included: it would
             burn frames on something nobody can see. */
          if (!e.isIntersecting) { ambient = false; halt(); return; }
          load();
          startAmbient();
        });
      }, { rootMargin: '1200px 0px', threshold: 0.01 });
      io.observe(stage);
    } else {
      load();
    }

    /* The centrepiece is worth fetching as soon as the page is otherwise
       idle, rather than waiting for the visitor to scroll within reach of
       it. Whichever trigger fires first wins; load() is guarded. */
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(load, { timeout: 2500 });
    } else {
      window.setTimeout(load, 1200);
    }

    /* ---- pointer ------------------------------------------------------ */

    stage.addEventListener('pointerdown', function (e) {
      /* Pressing before the sequence has landed used to do nothing at all,
         silently, which reads as "drag is broken" rather than "wait". Take
         the press, remember where it started, and start driving the moment
         the frames arrive. */
      load();
      if (!loaded) {
        pendingDrag = { x: e.clientX, id: e.pointerId };
        stage.classList.add('is-waiting');
        return;
      }
      driving = true;
      touched = true;
      ambient = false;
      velocity = 0;
      startX = e.clientX;
      dragFrom = pos;
      samples = [{ t: performance.now(), pos: pos }];
      stage.classList.add('is-driving');
      /* Capture so a fast drag that leaves the box keeps rotating. */
      try { stage.setPointerCapture(e.pointerId); } catch (err) {}
      schedule();
    });

    stage.addEventListener('pointermove', function (e) {
      if (!driving) return;
      /* Negated so dragging right turns the object to the right, the way a
         hand on a real turntable would. Only the position is set here; the
         paint happens on the next frame, so a burst of pointer events that
         arrive between two refreshes costs one draw rather than five. */
      pos = dragFrom - framesFor(e.clientX - startX);
      samples.push({ t: performance.now(), pos: pos });
      if (samples.length > 12) samples.shift();
      schedule();
    });

    function release(e) {
      if (pendingDrag) {
        pendingDrag = null;
        stage.classList.remove('is-waiting');
      }
      if (!driving) return;
      driving = false;
      stage.classList.remove('is-driving');
      try { stage.releasePointerCapture(e.pointerId); } catch (err) {}

      /* Carry the gesture on rather than stopping dead under their finger.
         The speed comes from the last stretch of the drag rather than the
         final pair of events, which are noisy enough to send a slow drag
         flying. A reduced-motion visitor gets none of it: a decaying spin is
         movement they are no longer steering. */
      velocity = 0;
      if (!reduce.matches && samples.length > 1) {
        var last = samples[samples.length - 1];
        var first = samples[0];
        for (var i = samples.length - 1; i >= 0; i--) {
          if (last.t - samples[i].t <= 100) first = samples[i];
        }
        var dt = (last.t - first.t) / 1000;
        if (dt > 0.008) {
          var cap = MAX_TURNS * frames.length;
          velocity = Math.max(-cap, Math.min(cap, (last.pos - first.pos) / dt));
          if (Math.abs(velocity) < 1) velocity = 0;
        }
      }
      samples = [];
      schedule();
    }

    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);
    /* A press taken before the frames arrived never got pointer capture, so
       the matching release can land anywhere on the page. */
    window.addEventListener('pointerup', release);

    /* ---- keyboard ------------------------------------------------------ */

    stage.addEventListener('keydown', function (e) {
      if (!loaded) return;
      var step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      /* The keyboard is driving too, so the ambient spin is done and any
         flick still decaying is overridden. Whole frames, since a key press
         is a discrete request rather than a gesture. */
      touched = true;
      ambient = false;
      velocity = 0;
      pos = Math.round(pos) + step * (e.shiftKey ? 5 : 1);
      paint();
      halt();
    });

    /* Tabbing onto it is intent to drive it, so the ambient spin gets out of
       the way and leaves the object where the visitor found it. */
    stage.addEventListener('focus', function () {
      touched = true;
      ambient = false;
      if (!velocity) halt();
    });
  }

  Array.prototype.forEach.call(stages, setup);
})();
